import React, { useEffect, useState } from 'react'
import {
	Box, Chip, Tooltip, Typography,
} from '@mui/material'
import { dsColorNew } from '@palette'
import {
	AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel, AppNote, AppBulkEditAction, AppTableSkeleton,
} from '@components'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { WarningAmberOutlined as WarningIcon } from 'Pages/DsRepricing/@components/icons'

import { useCategories } from 'Pages/DsRepricing/Setup/@services/useCategories'
import { setupApi } from 'Pages/DsRepricing/Setup/@services'

/**
 * "Suggest an order" — a proposal, never an application.
 *
 * Ordering forty ACRISS codes by hand is tedious enough that the
 * operator either skips the feature or gets it subtly wrong, so the
 * server proposes one: the median daily price this tenant's own market
 * showed over the last week where the sample supports it, the ACRISS
 * code's own structure where it does not.
 *
 * What this dialog is careful about is NOT applying it. The operator
 * sees what each position was based on — median, observation count,
 * and a warning where the market contradicts the code — and then
 * accepts or discards. A button that silently reorders a group that
 * was already right is a button nobody dares press.
 *
 * Category mode folds the same answer up a level: the market prices
 * ACRISS codes, not macros, so the call asks about every ACRISS inside
 * the selected categories and the categories are then ranked by where
 * their codes landed. One request, same evidence, no second endpoint.
 */

/** `{code → {code, name, sipps}}` from whatever tree it is handed.
 *  Passed in rather than read from a module constant so these stay
 *  pure — and so they stop describing a fleet the tenant does not have
 *  (Curro, 2026-09-28: "nada mock ya"). */
const macroIndex = (tree) => new Map((tree || []).map(m => [m.code, m]))

function median(numbers) {
	const xs = numbers.filter(n => Number.isFinite(n)).sort((a, b) => a - b)
	if (!xs.length) return null
	const mid = Math.floor(xs.length / 2)
	return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2
}

/** The ACRISS codes the server has to be asked about, for either mode. */
export function valuesToAsk(mode, items, tree) {
	if (mode !== 'category') return items
	const byCode = macroIndex(tree)
	const out = []
	for (const code of items) {
		for (const sipp of byCode.get(code)?.sipps ?? []) {
			if (!out.includes(sipp)) out.push(sipp)
		}
	}
	return out
}

/**
 * Rank the categories by where their own ACRISS codes landed.
 *
 * The mean position of a category's codes, rather than its cheapest or
 * its median code: a category is the whole set, and one cheap outlier
 * inside Luxury should not drag Luxury below Compact.
 */
export function foldToCategories(items, proposed, tree) {
	const byCode = macroIndex(tree)
	const at = new Map(proposed.map(p => [p.value, p]))
	const rows = items.map((code, fallback) => {
		const sipps = byCode.get(code)?.sipps ?? []
		const hits = sipps.map(s => at.get(s)).filter(Boolean)
		const priced = hits.filter(h => h.source === 'market')
		const positions = hits.map(h => h.position)
		return {
			value: code,
			label: byCode.get(code)?.name || code,
			median_price: median(priced.map(h => Number(h.median_price))),
			observations: hits.reduce((n, h) => n + (h.observations || 0), 0),
			source: priced.length ? 'market' : 'acriss_code',
			detail: sipps.length
				? `${priced.length} of ${sipps.length} codes priced`
				: 'no ACRISS codes in this category',
			_rank: positions.length
				? positions.reduce((a, b) => a + b, 0) / positions.length
				: Number.MAX_SAFE_INTEGER - items.length + fallback,
		}
	})
	rows.sort((a, b) => a._rank - b._rank)
	return rows.map((r, i) => ({ ...r, position: i }))
}

function money(value) {
	const n = Number(value)
	if (!Number.isFinite(n)) return '—'
	return `${n.toFixed(2)} €`
}

function SourceChip({ source }) {
	const market = source === 'market'
	return (
		<Chip size="small"
		      label={market ? 'market' : 'ACRISS code'}
		      title={market
		          ? 'Placed by the median price this market actually charged'
		          : 'Too few observations to trust a median — placed by what the code itself says'}
		      sx={toneChipSx(market ? 'info' : 'neutral')}/>
	)
}

export default function SuggestOrderDialog({ open, mode, items, onApply, onClose }) {
	const { tree } = useCategories()
	const [state, setState] = useState({ loading: true })

	useEffect(() => {
		if (!open) return
		let cancelled = false
		setState({ loading: true })
		setupApi.suggestConsistencyOrder(valuesToAsk(mode, items, tree))
			.then(res => {
				if (cancelled) return
				const proposed = res?.items || []
				setState({
					loading: false,
					observed: !!res?.observed,
					days: res?.days,
					rows: mode === 'category'
						? foldToCategories(items, proposed, tree)
						: proposed.map(p => ({ ...p, label: p.value })),
				})
			})
			.catch(err => {
				if (cancelled) return
				setState({ loading: false, error: err?.message || 'Could not reach the server' })
			})
		return () => { cancelled = true }
	// `items` is a fresh array on every render of the parent, so the
	// identity cannot be the dependency — it would refetch forever. The
	// contents are what the request is made of.
	// `tree` is in here on purpose: in category mode the request is
	// built from it, and a dialog opened before the catalogue lands
	// would otherwise ask the server about nothing and stay empty.
	}, [open, mode, items.join('\u0000'), tree])  // eslint-disable-line react-hooks/exhaustive-deps

	const rows = state.rows || []
	const unchanged = rows.length > 0
		&& rows.every((r, i) => r.value === items[i])

	return (
		<AppDialog open={open} onClose={onClose} maxWidth="sm" paperSx={setupScopeSx}>
			<AppDialogHeader icon="wand-magic-sparkles" title="Suggested order" onClose={onClose}
			                 subtitle="Cheapest first. Nothing changes until you apply it."/>

			<AppDialogBody sx={dialogBodySx}>
				{state.loading && <AppTableSkeleton rows={5} columns={5} sx={{ my: 1 }}/>}

				{state.error && (
					<AppNote severity="error" sx={{ mb: 2 }}>
						{state.error}
					</AppNote>
				)}

				{!state.loading && !state.error && (
					<>
						{state.observed ? (
							<AppNote severity="info" sx={{ mb: 2 }}>
								Based on the median daily price of the last {state.days} days
								of your own market, and on the ACRISS code where there were
								too few observations to trust a median.
							</AppNote>
						) : (
							<AppNote severity="warning" sx={{ mb: 2 }}>
								No market prices were available, so this order comes from the
								ACRISS codes alone. It has the right shape but it knows
								nothing about what your market actually charges.
							</AppNote>
						)}

						{unchanged && (
							<AppNote severity="success" sx={{ mb: 2 }}>
								This is the order the group already has.
							</AppNote>
						)}

						<Box component="table" sx={{ width: '100%', borderCollapse: 'collapse',
						                             fontSize: 12 }}>
							<Box component="thead">
								<Box component="tr" sx={{ '& th': {
									textAlign: 'left', fontWeight: 700, color: dsColorNew.textMuted,
									fontSize: 11, textTransform: 'uppercase',
									letterSpacing: '0.04em', pb: 0.75,
									borderBottom: '1px solid', borderColor: dsColorNew.grayBorder } }}>
									<Box component="th" sx={{ width: 28 }}>#</Box>
									<Box component="th">Value</Box>
									<Box component="th" sx={{ textAlign: 'right !important' }}>Median</Box>
									<Box component="th" sx={{ textAlign: 'right !important' }}>Obs.</Box>
									<Box component="th">Based on</Box>
								</Box>
							</Box>
							<Box component="tbody">
								{rows.map((row, i) => (
									<Box component="tr" key={row.value}
									     sx={{ '& td': { py: 0.6, borderBottom: '1px solid',
									                     borderColor: dsColorNew.pillTrackBg } }}>
										<Box component="td" sx={{ color: dsColorNew.textSubtle, fontWeight: 700 }}>
											{i + 1}
										</Box>
										<Box component="td" sx={{ fontWeight: 700,
										                          fontFamily: mode === 'category'
										                              ? 'inherit'
										                              : 'inherit' }}>
											{row.label}
											{row.warning && (
												<Tooltip title={row.warning}>
													<WarningIcon sx={{ fontSize: 14, ml: 0.5, mb: '-2px',
													                   color: dsColorNew.noteAmber }}/>
												</Tooltip>
											)}
										</Box>
										<Box component="td" sx={{ textAlign: 'right' }}>
											{money(row.median_price)}
										</Box>
										<Box component="td" sx={{ textAlign: 'right',
										                          color: dsColorNew.textMuted }}>
											{(row.observations || 0).toLocaleString()}
										</Box>
										<Box component="td">
											<SourceChip source={row.source}/>
											{row.detail && (
												<Typography component="span"
												            sx={{ ml: 0.75, fontSize: 11,
												                  color: dsColorNew.textMuted }}>
													{row.detail}
												</Typography>
											)}
										</Box>
									</Box>
								))}
							</Box>
						</Box>

						<Typography sx={{ display: 'block', mt: 1.5, fontSize: 12, color: dsColorNew.textMuted }}>
							A warning marks a value the market puts on one side and the ACRISS
							code on the other. Usually a thin sample, occasionally a real quirk
							of your market — worth a look before applying.
						</Typography>
					</>
				)}
			</AppDialogBody>

			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}>
					Discard
				</AppDialogCancel>
				<AppBulkEditAction disabled={state.loading || !!state.error || !rows.length || unchanged}
				                   onClick={() => onApply(rows.map(r => r.value))}>
					Apply this order
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}
